import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client/core';
import Modal from 'components/form/Modal';
import { ENABLE_CATEGORY_SELECTION } from 'components/constants';

export const TEAM_CATEGORIES = gql`
  query TeamCategories($id: ID!) {
    res: team(id: $id) {
      id
      locked
      endDate
      officialMatchTeamsessionCategory {
        id
      }
      fullTrainingTeamsessionCategory {
        id
      }
    }
  }
`;

const TeamCategoriesDialog = (props: { teamId: string, canSelect?: boolean }) => {
  const { teamId, canSelect } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [closed, setClosed] = useState(false);
  const onSelectedTeamPage = pathname.startsWith(`/teams/${teamId}`);

  const { data } = useQuery(TEAM_CATEGORIES, {
    variables: { id: teamId },
    fetchPolicy: 'cache-and-network',
    skip: !ENABLE_CATEGORY_SELECTION || !canSelect || !teamId,
  });

  useEffect(() => setClosed(false), [teamId, pathname]);

  if (!ENABLE_CATEGORY_SELECTION || !canSelect || onSelectedTeamPage || closed || !data?.res) {
    return null;
  }

  const missing = !data.res.officialMatchTeamsessionCategory || !data.res.fullTrainingTeamsessionCategory;
  const isTeamLockedOrExpired = data.res.locked || new Date(data.res.endDate) < new Date();

  if (!missing || isTeamLockedOrExpired) {
    return null;
  }

  return (
    <Modal
      visible
      noCancel
      buttonColor="primary"
      title={t('teams.dialog.categorySelectionTitle', 'categories: full training and official match')}
      content={(
        <Trans i18nKey="teams.dialog.categorySelectionText">
          Please specify which categories you use for
          {' '}
          <strong>full trainings</strong>
          {' '}
          and
          {' '}
          <strong>official matches</strong>
          .
          <br />
          <br />
          Mapping these labels allows the system to automatically calculate match distances
          (MD+/MD-), auto-create matches in your calendar upon tracking data upload, and power
          advanced features like the target wizard and upcoming AI-powered analytics.
        </Trans>
      )}
      buttonAction={t('teams.dialog.categorySelectionAction', 'go to team settings')}
      onClick={() => navigate(`/teams/${teamId}/?tab=settings&focus=categories`)}
      onClose={() => setClosed(true)}
    />
  );
};

export default TeamCategoriesDialog;
